import React, { useState, useEffect } from 'react'
import { Navigate, useNavigate, Link } from 'react-router-dom'
import { onAuthStateChanged, auth, hasBetaAccess, getUserSuspended, subscribeUserSuspended, signOutUser } from '../services'
import { useLoadingStore } from '../store/loadingStore'
import { Button } from '../design-system'
import './ProtectedRoute.css'

// Écran des comptes sans accès beta (compte Auth créé en contournant la
// popin /login : pas de claim `beta`, pas de doc users/{uid}). On signe le
// compte hors session pour rendre la main à la landing.
function BetaAccessDenied() {
  const navigate = useNavigate()

  useEffect(() => {
    signOutUser()
  }, [])

  return (
    <div className="ProtectedRoute__denied">
      <div className="ProtectedRoute__denied-panel">
        <h1 className="ProtectedRoute__denied-title">Beta access only</h1>
        <p className="ProtectedRoute__denied-text">
          This account doesn't have beta access. Enter your access code on the
          login page to continue.
        </p>
        <Button variant="primary" size="md" onClick={() => navigate('/')}>
          Back to home
        </Button>
      </div>
    </div>
  )
}

// Écran des comptes suspendus (issue #75 — enforcement de la politique de
// contenu) : le champ `suspended` du doc users/{uid} est posé serveur seul
// après des violations répétées. Déclenché AUSSI en pleine session via le
// listener temps réel subscribeUserSuspended (un run flaggé suspend pendant
// que l'utilisateur est sur le canvas). Le sign-out est différé de 4 s :
// l'utilisateur doit avoir le temps de lire le motif sur l'écran plein.
// Le lien « View guidelines » renvoie vers la charte publique
// /acceptable-use (route accessible déconnecté).
function SuspendedAccount() {
  useEffect(() => {
    const timer = setTimeout(() => {
      signOutUser()
    }, 4000)
    return () => clearTimeout(timer)
  }, [])

  return (
    <div className="ProtectedRoute__denied">
      <div className="ProtectedRoute__denied-panel">
        <h1 className="ProtectedRoute__denied-title">Account suspended</h1>
        <p className="ProtectedRoute__denied-text">
          This account has been permanently suspended for violating our
          content policy. Any further attempt may be reported to law
          enforcement.{' '}
          <Link className="ProtectedRoute__denied-link" to="/acceptable-use">
            View guidelines
          </Link>
        </p>
      </div>
    </div>
  )
}

// Cache de session du gate beta (garde UX — l'enforcement réel est serveur,
// R7) : sans lui, chaque montage d'un ProtectedRoute en navigation interne
// (ex. route-modale /settings par-dessus le canvas) rallumait le loader
// global plein écran le temps du round trip hasBetaAccess — flash visuel
// avant l'ouverture de la popin.
const betaAccessGrantedUids = new Set()
// Cache de session du gate « compte suspendu » (même raison : un round trip
// getUserSuspended par montage ferait flasher le loader). Un compte suspendu
// NE rentre JAMAIS dans ce cache — il est re-vérifié à chaque montage tant
// qu'il reste authentifié (l'écran SuspendedAccount le signe hors session).
const suspendedCheckedUids = new Set()

function ProtectedRoute({ children, requireEmailVerification = true }) {
  // Accès déjà validé cette session pour l'utilisateur courant : rendu des
  // enfants au premier paint, sans loader global ni round trip réseau.
  const hasSessionGrant = Boolean(auth.currentUser && betaAccessGrantedUids.has(auth.currentUser.uid))
  const [loading, setLoading] = useState(!hasSessionGrant)
  const [user, setUser] = useState(hasSessionGrant ? auth.currentUser : null)
  // null = vérification en cours ; le gate beta prime la vérification email
  const [betaAccess, setBetaAccess] = useState(hasSessionGrant ? true : null)
  // null = vérification en cours ; un compte suspendu prime tout le reste
  const [suspended, setSuspended] = useState(false)
  const startLoading = useLoadingStore((state) => state.startLoading)
  const stopLoading = useLoadingStore((state) => state.stopLoading)

  useEffect(() => {
    // Vérification complète uniquement si l'accès n'a pas déjà été validé
    // cette session ; sinon on garde juste l'abonnement auth (redirect
    // /login au sign-out) sans toucher au loader global.
    const skipCheck = Boolean(auth.currentUser && betaAccessGrantedUids.has(auth.currentUser.uid))
    if (!skipCheck) startLoading()
    // pending = le compteur de CE ProtectedRoute n'est pas encore soldé :
    // sans ce garde-fou, le cleanup décrémentait une seconde fois et
    // pouvait tuer un loader tenu par ailleurs (ex. création de projet).
    let pending = true
    // disposed = évite tout setState après démontage pendant les retries
    // asynchrones du gate beta ci-dessous.
    let disposed = false
    // Listener temps réel de suspension (issue #75) : la suspension peut
    // survenir EN PLEINE SESSION (les strikes serveur suspendent pendant un
    // run) — sans lui, elle n'était détectée qu'au rechargement. Posé dès
    // qu'un utilisateur non suspendu est authentifié ; à suspended = true,
    // l'écran SuspendedAccount prend le relais et signe hors session.
    let unsubscribeSuspended = null
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      pending = false
      // Gate « compte suspendu » (issue #75) : vérifié AVANT le gate beta
      // quand l'uid n'a pas déjà été contrôlé cette session — un compte
      // suspendu court-circuite tout le reste.
      let isSuspended = false
      if (currentUser && !suspendedCheckedUids.has(currentUser.uid)) {
        try {
          isSuspended = await getUserSuspended(currentUser.uid)
        } catch (err) {
          console.error('[AUTH] suspended check failed:', err)
          isSuspended = false
        }
        if (!isSuspended) suspendedCheckedUids.add(currentUser.uid)
      }
      // Gate beta : claim `beta` (posé par redeemBetaCode) ou doc users/{uid}
      // pré-existant (comptes antérieurs au gate, sans claim).
      let access = false
      if (currentUser && !isSuspended) {
        if (betaAccessGrantedUids.has(currentUser.uid)) {
          access = true
        } else {
          // Grace period d'inscription : juste après un signup beta (Google
          // ou email), redeemBetaCode / ensureUserProfile peuvent encore
          // être en vol — on retente ~3 fois à ~1 s d'intervalle avant de
          // conclure au refus. Un compte réellement non autorisé finit sur
          // le même BetaAccessDenied qu'avant, après les retries.
          for (let attempt = 0; attempt < 3 && !access; attempt++) {
            try {
              access = await hasBetaAccess(currentUser)
            } catch (err) {
              console.error('[AUTH] beta access check failed:', err)
              access = false
            }
            if (!access && attempt < 2) {
              await new Promise((resolve) => setTimeout(resolve, 1000))
            }
          }
          if (access) betaAccessGrantedUids.add(currentUser.uid)
        }
      }
      if (disposed) return
      // (Ré)abonnement au statut de suspension : un changement d'utilisateur
      // ou un sign-out ferme le listener précédent.
      unsubscribeSuspended?.()
      unsubscribeSuspended = null
      if (currentUser && !isSuspended) {
        unsubscribeSuspended = subscribeUserSuspended(currentUser.uid, (nowSuspended) => {
          if (nowSuspended && !disposed) setSuspended(true)
        })
      }
      setSuspended(isSuspended)
      setBetaAccess(access)
      setUser(currentUser)
      setLoading(false)
      if (!skipCheck) stopLoading()
    })

    return () => {
      disposed = true
      unsubscribe()
      unsubscribeSuspended?.()
      if (pending && !skipCheck) stopLoading()
    }
  }, [startLoading, stopLoading])

  if (loading || (user && betaAccess === null)) {
    return null
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (suspended) {
    return <SuspendedAccount />
  }

  if (!betaAccess) {
    return <BetaAccessDenied />
  }

  if (requireEmailVerification && !user.emailVerified) {
    return <Navigate to="/email-verification" replace />
  }

  return children
}

export default ProtectedRoute
